import AddIcon from '@mui/icons-material/Add';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import UploadIcon from '@mui/icons-material/Upload';
import {
  Box, Button,
} from '@mui/material';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import { useSnackbar } from 'notistack';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { OrderAttrFilter } from 'src/components/dispatch/orderAttrFilter';
import { DisplayMetric } from 'src/components/dispatch/unassignedDisplay/displayMetricKeys';
import { ShipmentOrderForm } from 'src/components/V2ShipmentOrderForm';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import DisplayMetricToggle from 'src/pages/ConvenienceDispatches/DisplayMetricToggle';
import { buildTruckShiftsPath } from 'src/pages/TruckShifts/truckShiftGrid';

import { resolveDateRangeChange } from './resolveDateRangeChange';

type Props = {
  /** 画面遷移。Container の useNavigate を包んで渡す */
  onNavigate: (path: string) => void;
  /** 日付範囲ピッカーの現在値。URL パラメータ由来 */
  startOn: string;
  endOn: string;
  /**
   * 日付が確定したときに呼ぶ。`/map` は日付を URL に持つので、呼び出し側は
   * `navigate('/map/{startOn}/{endOn}')` する（`/plans` と同じ方式）。
   * 判定（逆転・10 日制限）は `resolveDateRangeChange` が済ませてある。
   */
  onDateRangeChange: (startOn: string, endOn: string) => void;
  /** 自動配車ボタン。props 名はカード配車 (ConvenienceDispatchToolbar.tsx) と揃える */
  onAutoDispatch: () => void;
  isAutoDispatchDisabled: boolean;
  autoDispatchTooltip?: string;
  /**
   * シナリオ配車ボタン。自動配車と違い、ライセンス (use_scenario_planning) が
   * 無ければボタン自体を描かない（disabled にするのではない。設計書 :161）。
   */
  showScenarioPlanning: boolean;
  onScenarioDispatch: () => void;
  isScenarioDispatchDisabled: boolean;
  scenarioDispatchTooltip?: string;
  /**
   * 表示項目トグル。右パネル上部からここへ移した（サンプル 7.png の 1 行に集約）。
   * **1 箇所にだけ置く**ので、右パネル側に復活させないこと。
   */
  displayMetrics: DisplayMetric[];
  onDisplayMetricsChange: (value: DisplayMetric[]) => void;
  customInputFieldNames: string[];
  isLicenseReady: boolean;
  /** 表示対象（配送形態・区間種別）の絞り込み。表示項目トグルの中に置く */
  orderAttrFilter: OrderAttrFilter;
  onOrderAttrFilterChange: (next: OrderAttrFilter) => void;
};

/**
 * `/map` の横断ツールバー。
 *
 * flexWrap で折り返す。**個々のボタンを隠さない**（隠すと機能に到達できなくなる）。
 * 高さは内容なりに伸びるので、親は flex: 0 0 auto で受けること。
 */
const MapToolbar: FC<Props> = memo(({
  onNavigate,
  startOn,
  endOn,
  onDateRangeChange,
  onAutoDispatch,
  isAutoDispatchDisabled,
  autoDispatchTooltip,
  showScenarioPlanning,
  onScenarioDispatch,
  isScenarioDispatchDisabled,
  scenarioDispatchTooltip,
  displayMetrics,
  onDisplayMetricsChange,
  orderAttrFilter,
  onOrderAttrFilterChange,
  customInputFieldNames,
  isLicenseReady,
}) => {
  const { enqueueSnackbar } = useSnackbar();

  // P4-a では「MapToolbar は state を持たない」契約にしていたが、案件作成ダイアログの
  // 開閉はここで完結し外へ出す理由が無いのでこの契約を破る。カード配車
  // (ConvenienceDispatchToolbar.tsx:241) も同じ形。
  const [newShipmentDialogOpen, setNewShipmentDialogOpen] = useState(false);

  // ピッカーは片方だけ選ばれた中間状態を持つので、確定値（props）とは別に保持する。
  // カード配車（ConvenienceDispatchToolbar.tsx:120-130）と同じ形。
  const [selectedRange, setSelectedRange] = useState<DateRange<Date>>([
    startOn ? new Date(startOn) : null,
    endOn ? new Date(endOn) : null,
  ]);

  useEffect(() => {
    setSelectedRange([
      startOn ? new Date(startOn) : null,
      endOn ? new Date(endOn) : null,
    ]);
  }, [startOn, endOn]);

  const handleDateRange = useCallback((range: DateRange<Date>) => {
    // 片方だけ選んだ時点でも表示は追随させる（確定は resolve が判断する）
    setSelectedRange((prev) => [range[0] ?? prev[0], range[1] ?? prev[1]]);

    const result = resolveDateRangeChange([range[0] ?? null, range[1] ?? null]);
    if (result.kind === 'ignore') return;
    if (result.kind === 'reject') {
      enqueueSnackbar(result.message);
      return;
    }

    ReactGA.event('select', {
      screen_name: SCREEN_NAMES.PLANNING_MAP,
      button_name: '日付範囲',
      label: `${result.startOn}~${result.endOn}`,
    });
    onDateRangeChange(result.startOn, result.endOn);
  }, [enqueueSnackbar, onDateRangeChange]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        // 地図が透けないよう不透明にする（スクロール時に下の内容が見えると読みにくい）
        bgcolor: '#fff',
        borderBottom: '1px solid hsl(var(--border))',
      }}
    >
      {/* 左寄せ: 日付・稼働表 */}
      <DateRangePicker
        value={selectedRange}
        onOpen={() => ReactGA.event('click', {
          screen_name: SCREEN_NAMES.PLANNING_MAP,
          button_name: '日付選択',
        })}
        onChange={handleDateRange}
        slotProps={{ textField: { size: 'small', sx: { p: 0, m: 0 } } }}
      />

      {/* 稼働表 (/truck-shifts)。カード配車 (ConvenienceDispatchToolbar.tsx:389-407) と同じ形。
          backTo に 'map' を渡すので、稼働表の「戻る」で /map の同じ期間へ戻れる
          (truckShiftGrid.ts の backDestinationPath に map の分岐を足してある)。 */}
      <Button
        size="small"
        onClick={() => {
          if (!startOn) return;
          ReactGA.event('click', {
            screen_name: SCREEN_NAMES.PLANNING_MAP,
            button_name: '稼働表',
          });
          onNavigate(buildTruckShiftsPath({ startOn, endOn: endOn || startOn, backTo: 'map' }));
        }}
        disabled={!startOn}
        sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
      >
        稼働表
      </Button>

      {/* 左右を分ける。以降はすべて右寄せ */}
      <Box sx={{ flex: 1 }} />

    {/* ライセンス (use_scenario_planning) が無ければボタン自体を描かない。
        自動配車は常に描いて disabled にするが、シナリオ配車は存在しないものとして扱う
        (設計書 :161。「描かない」と「disabled」を混同しない)。

        並びは「シナリオ配車 → 自動配車 → インポート」。サンプル実装に合わせて
        シナリオ配車を自動配車の左に置く (MapToolbar.test.tsx が順序を固定している)。 */}
    {showScenarioPlanning && (
      <Button
        size="small"
        startIcon={<PlayCircleIcon sx={{ fontSize: 14 }} />}
        onClick={() => {
          ReactGA.event('click', {
            screen_name: SCREEN_NAMES.PLANNING_MAP,
            button_name: 'シナリオ配車',
          });
          onScenarioDispatch();
        }}
        disabled={isScenarioDispatchDisabled}
        title={scenarioDispatchTooltip}
        sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
      >
        シナリオ配車
      </Button>
    )}

    <Button
      size="small"
      startIcon={<LocalShippingIcon sx={{ fontSize: 14 }} />}
      onClick={() => {
        ReactGA.event('click', {
          screen_name: SCREEN_NAMES.PLANNING_MAP,
          button_name: '自動配車',
        });
        onAutoDispatch();
      }}
      disabled={isAutoDispatchDisabled}
      title={autoDispatchTooltip}
      // 押せる状態を色で示す（サンプル PlanToolbar.tsx:443）。無効時まで
      // contained にすると、押せないのに主要操作に見える
      variant={isAutoDispatchDisabled ? 'text' : 'contained'}
      sx={{
        textTransform: 'none',
        fontSize: 12,
        ...(isAutoDispatchDisabled ? { color: 'text.secondary' } : {}),
      }}
    >
      自動配車
    </Button>

    <Button
      size="small"
      startIcon={<UploadIcon sx={{ fontSize: 14 }} />}
      onClick={() => {
        ReactGA.event('click', {
          screen_name: SCREEN_NAMES.PLANNING_MAP,
          button_name: 'インポート',
        });
        onNavigate('/import');
      }}
      sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
    >
      インポート
      </Button>

      {/* 案件作成（V3Plans・カード配車と同じ ShipmentOrderForm ダイアログ） */}
      <Button
        size="small"
        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
        onClick={() => {
          if (!startOn) return;
          ReactGA.event('click', {
            screen_name: SCREEN_NAMES.PLANNING_MAP,
            button_name: '案件作成',
          });
          setNewShipmentDialogOpen(true);
        }}
        disabled={!startOn}
        sx={{ textTransform: 'none', fontSize: 12, color: 'text.secondary' }}
      >
        案件作成
      </Button>

      {/* startOn が無いまま開くと baseDate が Invalid Date になる
          （カード配車 ConvenienceDispatchToolbar.tsx:642 の `startOn &&` を写したもの） */}
      {startOn && newShipmentDialogOpen && (
        <ShipmentOrderForm
          baseDate={new Date(startOn)}
          startOn={startOn}
          newShipmentDialogOpen={newShipmentDialogOpen}
          setNewShipmentDialogOpen={setNewShipmentDialogOpen}
        />
      )}

      {/* 運行状況表示（車両位置のライセンスがある会社だけ）。
          旧地図配車 V3Plans presenters/ActionsOnMapPresenter.tsx:30-40 */}
      {/* 表示項目（右寄せの末尾）。右パネル上部から移設したもので、画面内で 1 箇所だけ */}
      <DisplayMetricToggle
        value={displayMetrics}
        onChange={onDisplayMetricsChange}
        orderAttrFilter={orderAttrFilter}
        onOrderAttrFilterChange={onOrderAttrFilterChange}
        customInputFieldNames={customInputFieldNames}
        isLicenseReady={isLicenseReady}
        screenName={SCREEN_NAMES.PLANNING_MAP}
      />
    </Box>
  );
});

export default MapToolbar;
